/**
 * What a selection of cards would do at the seaport.
 *
 * `legalCommands` narrows two families on purpose — it offers one representative
 * `display` and a sample of ship sets, because the payload space is combinatorial
 * and the choice belongs to the client (see the contract in the engine's
 * `legal.ts`). This module is that client half: given the cards a player has
 * ticked, it builds the commands for them and says what each is worth. Every
 * command it can produce is one `apply` accepts, which `seaport.test.ts` checks
 * exhaustively rather than by sampling.
 */
import {
  DISPLAY_MAX,
  MANIFESTS,
  setSize,
  shipVp,
  type Command,
  type PlayerId,
  type ShipSet,
  type View,
} from '@trajan/engine';

/** One thing a player can put into a set: a card in hand, or a wild tile standing in for one. */
export interface Chosen {
  id: string;
  type: string;
  wild: boolean;
}

export interface Selection {
  cardIds: string[];
  /** Wild-commodity forum tiles, each standing in for a commodity. */
  wilds: { tileId: string; asType: string }[];
}

export interface ShipOffer {
  shipId: string;
  set: ShipSet;
  side: 'colour' | 'grey';
  /** Cards (or pairs) the set is worth on the ship's table; null if refused. */
  units: number | null;
  vp: number;
  /** What this ship wants, or why it will not take this selection. */
  why: string;
  command: Command | null;
}

export interface SeaportPlan {
  chosen: Chosen[];
  /** The display command for this selection, when it is a legal display. */
  display: { command: Command; cards: number } | null;
  ships: ShipOffer[];
}

export const EMPTY_SELECTION: Selection = { cardIds: [], wilds: [] };

const WANTS: Record<ShipSet, string> = {
  identical: 'up to four of the same commodity',
  different: 'up to four different commodities',
  pairs: 'one, two or three pairs — two of each commodity',
};

/** What the ticked cards would do: displayed, or loaded onto each ship. */
export function planSeaport(view: View, seat: PlayerId, selection: Selection): SeaportPlan {
  const hand = view.players[seat]?.hand ?? [];
  const chosen: Chosen[] = [
    ...selection.cardIds.flatMap((id) => {
      const card = hand.find((c) => c.id === id);
      return card ? [{ id: card.id, type: card.type, wild: false }] : [];
    }),
    ...selection.wilds.map((w) => ({ id: w.tileId, type: w.asType, wild: true })),
  ];
  const types = chosen.map((c) => c.type);

  // Displaying plays cards from hand; a wild tile is not a card, so it cannot go.
  const display =
    selection.wilds.length === 0 &&
    selection.cardIds.length > 0 &&
    selection.cardIds.length <= DISPLAY_MAX
      ? {
          command: {
            type: 'TakeAction',
            kind: 'seaport',
            payload: { option: 'display', cardIds: [...selection.cardIds] },
          } as Command,
          cards: selection.cardIds.length,
        }
      : null;

  const ships: ShipOffer[] = view.board.ships.map((ship) => {
    const table = MANIFESTS.ships.find((s) => s.id === ship.id);
    const set = table?.set ?? ship.set;
    const units = types.length > 0 && table ? setSize(set, types) : null;
    const vp = units === null ? 0 : shipVp(ship, units);
    return {
      shipId: ship.id,
      set,
      side: ship.side,
      units,
      vp,
      why:
        types.length === 0
          ? `wants ${WANTS[set]}`
          : units === null
            ? `will not take this set — wants ${WANTS[set]}`
            : `${units} ${set === 'pairs' ? (units === 1 ? 'pair' : 'pairs') : units === 1 ? 'card' : 'cards'} on the ${ship.side === 'grey' ? 'grey' : 'full'} side`,
      command:
        units === null
          ? null
          : ({
              type: 'TakeAction',
              kind: 'seaport',
              payload: {
                option: 'ship',
                ship: ship.id,
                cardIds: [...selection.cardIds],
                ...(selection.wilds.length > 0 ? { wilds: [...selection.wilds] } : {}),
              },
            } as Command),
    };
  });

  return { chosen, display, ships };
}

/** The commodity types a wild tile may stand in for. */
export const COMMODITY_TYPES: readonly string[] = [
  ...new Set(MANIFESTS.tiles.commodity.map((c) => c.type)),
].sort();

/** Add or remove a hand card from the selection. */
export function toggleCard(selection: Selection, cardId: string): Selection {
  return selection.cardIds.includes(cardId)
    ? { ...selection, cardIds: selection.cardIds.filter((id) => id !== cardId) }
    : { ...selection, cardIds: [...selection.cardIds, cardId] };
}

/** Add, retype or remove a wild tile. Passing the type it already has removes it. */
export function toggleWild(selection: Selection, tileId: string, asType: string): Selection {
  const current = selection.wilds.find((w) => w.tileId === tileId);
  if (!current) return { ...selection, wilds: [...selection.wilds, { tileId, asType }] };
  if (current.asType === asType) {
    return { ...selection, wilds: selection.wilds.filter((w) => w.tileId !== tileId) };
  }
  return {
    ...selection,
    wilds: selection.wilds.map((w) => (w.tileId === tileId ? { tileId, asType } : w)),
  };
}
